import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout';
import { Toast, SearchableSelect } from '../../components';
import { carService, voyageDropdownService, VoyageDropdownItem } from '../../services';
import { useToast } from '../../hooks/useToast';
import * as XLSX from 'xlsx';

interface FailedCar {
  chassis: string;
  reason: string;
}

const BulkChangeVoyagePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [voyages, setVoyages] = useState<VoyageDropdownItem[]>([]);
  const [fromVoyage, setFromVoyage] = useState<VoyageDropdownItem | null>(null);
  const [toVoyage, setToVoyage] = useState<VoyageDropdownItem | null>(null);
  const [chassisNumbers, setChassisNumbers] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string>('');
  const [failedCars, setFailedCars] = useState<FailedCar[]>([]);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [lastResult, setLastResult] = useState<{ updated: number; failed: number } | null>(null);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkAddText, setBulkAddText] = useState('');

  // Load voyages using lightweight dropdown endpoint
  useEffect(() => {
    const loadVoyages = async () => {
      try {
        const data = await voyageDropdownService.getAll({ limit: 1000 });
        setVoyages(data);
      } catch (error: any) {
        console.error('Error loading voyages:', error);
        showToast('Failed to load voyages', 'error');
      }
    };
    loadVoyages();
  }, [showToast]);

  // Handle file upload and parse Excel
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError('');
    setChassisNumbers([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<{ [key: string]: any }>(firstSheet, { header: 1 });
      
      // Extract chassis numbers from first column (skip header if present)
      const chassis: string[] = [];
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] as any[];
        if (row && row[0]) {
          const value = String(row[0]).trim();
          // Skip header-like values
          if (value && !value.toLowerCase().includes('chassis') && value.length > 0) {
            chassis.push(value);
          }
        }
      }

      if (chassis.length === 0) {
        setParseError('No chassis numbers found in the Excel file. Make sure they are in the first column.');
        return;
      }

      setChassisNumbers(chassis);
    } catch (error) {
      console.error('Error parsing file:', error);
      setParseError('Failed to parse file. Make sure it is a valid Excel file (.xlsx or .xls).');
    }
  };

  // Handle bulk add from pasted text
  const handleBulkAddSubmit = () => {
    const lines = bulkAddText.split(/[\n,]+/).map(s => s.trim()).filter(s => s.length > 0);
    if (lines.length === 0) {
      setParseError('No chassis numbers found. Enter one per line or comma-separated.');
      return;
    }
    setChassisNumbers(lines);
    setFileName('Manual entry');
    setParseError('');
    setBulkAddText('');
    setShowBulkAddModal(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!fromVoyage) {
      showToast('Please select a From Voyage', 'error');
      return;
    }
    if (!toVoyage) {
      showToast('Please select a To Voyage', 'error');
      return;
    }
    if (fromVoyage.id === toVoyage.id) {
      showToast('From and To voyage must be different', 'error');
      return;
    }
    if (chassisNumbers.length === 0) {
      showToast('Please upload an Excel file with chassis numbers', 'error');
      return;
    }

    setSubmitting(true);
    setFailedCars([]);
    setLastResult(null);
    
    try {
      const result = await carService.bulkChangeVoyageByChassis(
        chassisNumbers,
        fromVoyage.id,
        toVoyage.id
      ) as { message: string; updated_count: number; failed?: FailedCar[] };
      
      const failed = result.failed || [];
      setLastResult({ updated: result.updated_count, failed: failed.length });
      
      if (failed.length > 0) {
        setFailedCars(failed);
        setShowFailedModal(true);
        showToast(`Moved ${result.updated_count} cars. ${failed.length} failed.`, failed.length > 0 ? 'error' : 'success');
      } else {
        showToast(result.message, 'success');
      }
      
      // Reset form
      setChassisNumbers([]);
      setFileName('');
      setFromVoyage(null);
      setToVoyage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error changing voyage:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to change voyage';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Build voyage options for React Select
  const fromVoyageOptions = (toVoyage
    ? voyages.filter(v => v.id !== toVoyage.id)
    : voyages
  ).map(v => ({ value: v.id, label: `${v.voyage_code} - ${v.vessel_description}` }));

  const toVoyageOptions = (fromVoyage
    ? voyages.filter(v => v.id !== fromVoyage.id)
    : voyages
  ).map(v => ({ value: v.id, label: `${v.voyage_code} - ${v.vessel_description}` }));

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Bulk Change Voyage
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Upload an Excel file with chassis numbers to move multiple vehicles from one voyage to another.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload Excel File (Chassis Numbers in First Column)
            </label>
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 dark:text-gray-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  dark:file:bg-gray-700 dark:file:text-gray-300
                  dark:hover:file:bg-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowBulkAddModal(true)}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                Bulk Add
              </button>
            </div>
            {parseError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{parseError}</p>
            )}
            {fileName && chassisNumbers.length > 0 && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                Found {chassisNumbers.length} chassis numbers in "{fileName}"
              </p>
            )}
          </div>

          {/* Preview chassis numbers */}
          {chassisNumbers.length > 0 && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Chassis Numbers Preview ({chassisNumbers.length} total)
              </h3>
              <div className="max-h-40 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {chassisNumbers.slice(0, 50).map((chassis, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-2 py-1 text-xs font-mono bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white"
                    >
                      {chassis}
                    </span>
                  ))}
                  {chassisNumbers.length > 50 && (
                    <span className="inline-block px-2 py-1 text-xs text-gray-500">
                      ...and {chassisNumbers.length - 50} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Voyage Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Voyage
              </label>
              <SearchableSelect
                options={fromVoyageOptions}
                value={fromVoyage?.id || ''}
                onChange={(value) => setFromVoyage(voyages.find(v => v.id === value) || null)}
                placeholder="Select source voyage..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Voyage
              </label>
              <SearchableSelect
                options={toVoyageOptions}
                value={toVoyage?.id || ''}
                onChange={(value) => setToVoyage(voyages.find(v => v.id === value) || null)}
                placeholder="Select destination voyage..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={submitting || !fromVoyage || !toVoyage || chassisNumbers.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Processing...' : `Move ${chassisNumbers.length} Vehicles`}
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Add Modal */}
      {showBulkAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Bulk Add Chassis Numbers
                </h2>
                <button
                  onClick={() => setShowBulkAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Paste chassis numbers below (one per line or comma-separated)
              </p>
            </div>
            <div className="p-6">
              <textarea
                value={bulkAddText}
                onChange={(e) => setBulkAddText(e.target.value)}
                placeholder="CHASSIS001&#10;CHASSIS002&#10;CHASSIS003"
                rows={10}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowBulkAddModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Add Chassis Numbers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Failed Cars Modal */}
      {showFailedModal && failedCars.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Operation Result
                </h2>
                <button
                  onClick={() => setShowFailedModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {lastResult && (
                <div className="mt-2 flex gap-4">
                  <span className="text-green-600 dark:text-green-400">
                    ✓ {lastResult.updated} moved successfully
                  </span>
                  <span className="text-red-600 dark:text-red-400">
                    ✗ {lastResult.failed} failed
                  </span>
                </div>
              )}
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Failed Chassis Numbers:
              </h3>
              <div className="space-y-2">
                {failedCars.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                  >
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                      {item.chassis}
                    </span>
                    <span className="text-sm text-red-600 dark:text-red-400">
                      {item.reason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  const text = failedCars.map((item) => item.chassis).join('\n');
                  navigator.clipboard.writeText(text);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Copy Failed
              </button>
              <button
                onClick={() => setShowFailedModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </Layout>
  );
};

export default BulkChangeVoyagePage;
